import {AppThemePaletteEntryData} from "../orchestration/app-entity.model";

export class Color {
    public static WHITE: Color = Color.fromRGB(255, 255, 255);
    public static BLACK: Color = Color.fromRGB(0, 0, 0);

    red: number;
    green: number;
    blue: number;
    hue: number;
    saturation: number;
    lightness: number;
    lightness601: number;
    relativeLuminance: number;

    public static darkLightRGB(paletteEntry: AppThemePaletteEntryData): [string, string] {
        const background: Color = Color.fromString(paletteEntry.colorPair.backgroundColor);
        const foreground: Color = Color.fromString(paletteEntry.colorPair.foregroundColor);
        const bgRGB: string = background ? background.toRGBString() : undefined;
        const fgRGB: string = foreground ? foreground.toRGBString() : undefined;
        const bgLightness = background ? background.lightness601 : 0;
        const fgLightness = foreground ? foreground.lightness601 : 0;
        return bgLightness < fgLightness ? [bgRGB, fgRGB] : [fgRGB, bgRGB];
    }

    public static themeRGB(colorName: string): string {
        return "var(--ovd-theme-color-" + colorName + ", var(--woa-theme-color-" + colorName + "))";
    }

    // noinspection JSUnusedGlobalSymbols
    public static themeColor(colorName: string, alpha: string = "1") {
        return "rgba(" + Color.themeRGB(colorName) + ", " + alpha + ")";
    }

    public static colorStringToRGB(colorString: string): string {
        const color: Color = Color.fromString(colorString);
        return color === undefined ? undefined : color.toRGBString();
    }

    public static colorStringToShaderRGB(colorString: string, inverse: boolean = false): string {
        const color: Color = Color.fromString(colorString);
        return color === undefined ? undefined : (color.lightness601 < 0.5 !== inverse) ? "100, 100, 100" : "255, 255, 255";
    }

    public static fromString(colorString: string): Color {
        if (!colorString || !colorString.startsWith("#")) {
            return undefined;
        }
        if (colorString.length >= 4 && colorString.length <= 5) {
            const r: number = parseInt(colorString.substring(1, 2) + colorString.substring(1, 2), 16);
            const g: number = parseInt(colorString.substring(2, 3) + colorString.substring(2, 3), 16);
            const b: number = parseInt(colorString.substring(3, 4) + colorString.substring(3, 4), 16);
            return Color.fromRGB(r, g, b);
        }
        if (colorString.length >= 7 && colorString.length <= 9) {
            const r: number = parseInt(colorString.substring(1, 3), 16);
            const g: number = parseInt(colorString.substring(3, 5), 16);
            const b: number = parseInt(colorString.substring(5, 7), 16);
            return Color.fromRGB(r, g, b);
        }
        return undefined;
    }

    public static fromRGB(red: number, green: number, blue: number): Color {
        const color: Color = new Color();
        color.red = red;
        color.green = green;
        color.blue = blue;

        const r = red / 255.0;
        const g = green / 255.0;
        const b = blue / 255.0;
        const max: number = Math.max(r, g, b);
        const min: number = Math.min(r, g, b);
        const chroma: number = max - min;

        color.lightness = (min + max) / 2;
        color.saturation = chroma == 0 ? 0 : color.lightness > 0.5 ? chroma / (2 - max - min) : chroma / (max + min);
        color.hue = chroma === 0 ? 0 : (max === r ? ((g - b) / chroma) % 6 : max === g ? ((b - r) / chroma) + 2 : ((r - g) / chroma) + 4) * 60;
        if (color.hue < 0) {
            color.hue += 360.0;
        }
        color.calculateLightness901();
        color.calculateRelativeLuminance();

        return color;
    }

    public static fromHSL(hue: number, saturation: number, lightness: number): Color {
        const color: Color = new Color();
        color.hue = hue < 0 ? hue + 360 : hue > 360 ? hue - 360 : hue;
        color.saturation = saturation;
        color.lightness = lightness < 0 ? 0 : lightness > 1 ? 1 : lightness;

        let r: number;
        let g: number;
        let b: number;
        if (color.saturation === 0) {
            r = color.lightness;
            g = color.lightness;
            b = color.lightness;
        } else {
            const q: number = color.lightness < 0.5 ? color.lightness * (1 + color.saturation) : color.lightness + color.saturation - (color.lightness * color.saturation);
            const p = 2 * color.lightness - q;
            r = Color.hueToRgb(p, q, color.hue + 120);
            g = Color.hueToRgb(p, q, color.hue);
            b = Color.hueToRgb(p, q, color.hue - 120);
        }
        color.red = Math.round(r * 255);
        color.green = Math.round(g * 255);
        color.blue = Math.round(b * 255);
        color.calculateLightness901();
        color.calculateRelativeLuminance();

        return color;
    }

    private static hexString(n: number): string {
        const s: string = "0" + n.toString(16);
        return s.slice(s.length - 2);
    }

    private static hueToRgb(p: number, q: number, t: number): number {
        if (t < 0) {
            t += 360;
        } else if (t > 360) {
            t -= 360;
        }
        return t < 60 ? p + ((q - p) * t / 60) : t < 180 ? q : t < 240 ? p + ((q - p) * (240 - t) / 60) : p;
    }

    private static luminanceValue(rgb: number): number {
        return rgb < 0.03928 ? rgb / 12.92 : Math.pow((rgb + 0.055) / 1.055, 2.4);
    }

    private calculateLightness901(): void {
        this.lightness601 = ((this.red * 0.299) + (this.green * 0.587) + (this.blue * 0.114)) / 255;
    }

    private calculateRelativeLuminance(): void {
        this.relativeLuminance = Color.luminanceValue(this.red / 255.0) * 0.2126 + Color.luminanceValue(this.green / 255.0) * 0.7152 + Color.luminanceValue(this.blue / 255.0) * 0.0722;
    }

    public toString(): string {
        return "#" + Color.hexString(this.red) + Color.hexString(this.green) + Color.hexString(this.blue);
    }

    public toRGBString(): string {
        return this.red + ", " + this.green + ", " + this.blue;
    }

    public applyAlpha(background: Color, alpha: number): Color {
        return Color.fromRGB(
            Math.floor(background.red * (1 - alpha) + this.red * alpha),
            Math.floor(background.green * (1 - alpha) + this.green * alpha),
            Math.floor(background.blue * (1 - alpha) + this.blue * alpha));
    }

    // noinspection JSUnusedGlobalSymbols
    public complement(): Color {
        return Color.fromRGB(255 - this.red, 255 - this.green, 255 - this.blue);
    }

    // noinspection JSUnusedGlobalSymbols
    public contrast(maxLightnessDifference: number = 1.0): Color {
        return this.lightness601 > 0.5 ? Color.BLACK.withLightness(this.lightness - maxLightnessDifference) : Color.WHITE.withLightness(this.lightness + maxLightnessDifference);
    }

    public rotate(degrees: number): Color {
        return Color.fromHSL(this.hue + degrees, this.saturation, this.lightness);
    }

    public lighten(percent: number, toAtLeast: number = 0): Color {
        return Color.fromHSL(this.hue, this.saturation, Math.min(Math.max(this.lightness + ((1.0 - this.lightness) * percent / 100), toAtLeast), 1));
    }

    public darken(percent: number, toAtMost: number = 1): Color {
        return Color.fromHSL(this.hue, this.saturation, Math.max(Math.min(this.lightness - (this.lightness * percent / 100), toAtMost), 0));
    }

    public withLightness(lightness: number): Color {
        return Color.fromHSL(this.hue, this.saturation, lightness);
    }

    public getContrastRatio(other: Color): number {
        return (Math.max(this.relativeLuminance, other.relativeLuminance) + 0.05) / (Math.min(this.relativeLuminance, other.relativeLuminance) + 0.05);
    }

    // noinspection JSUnusedGlobalSymbols
    public adjustForContrast(other: Color, minContrast: number, allowInversion = true): Color {
        if (this.getContrastRatio(other) >= minContrast) {
            return this;
        }
        const lighten: boolean = allowInversion ?
            (this.lightness >= other.lightness && Color.WHITE.getContrastRatio(other) >= minContrast) || Color.BLACK.getContrastRatio(other) < minContrast :
            this.lightness >= other.lightness;

        let adjustedColor: Color = this;
        while (true) {
            const lightness: number = adjustedColor.lightness;
            adjustedColor = lighten ? adjustedColor.lighten(1, lightness + 0.001) : adjustedColor.darken(1, lightness - 0.001);
            if (adjustedColor.lightness === lightness || adjustedColor.getContrastRatio(other) >= minContrast) {
                break;
            }
        }
        return adjustedColor;
    }
}



// WEBPACK FOOTER //
// ./src/app/ui-elements/color.ts